import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import { MatchGameExercise, MatchSide } from '../../../student/mockLessonContent';
import { BLUE, cardSx, GREEN, INTER, NAVY, POPPINS, RED, SLATE, SUCCESS_TEXT, SURFACE_SOFT } from '../../../student/studentStyles';
import ExerciseLabel from '../../../student/components/exercises/ExerciseLabel';
import { useExerciseLayout } from './exerciseLayout';

interface MatchGameExerciseCardProps {
    exercise: MatchGameExercise;
    onReady: () => void;
}

type Pair = MatchGameExercise['pairs'][number];

const WRONG_FLASH_MS = 700;

const sideLabels: Record<MatchSide, string> = {
    character: 'CHARACTERS',
    pinyin: 'PINYIN',
    english: 'MEANING',
    image: 'PICTURES',
    audio: 'AUDIO',
};

// Deterministic ordering so the right column stays put across re-renders.
function shiftedOrder(length: number, seed: string): number[] {
    const offset = 1 + (seed.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0) % Math.max(1, length - 1));
    return Array.from({ length }, (_, index) => (index + offset) % length);
}

function renderSide(pair: Pair, side: MatchSide) {
    if (side === 'image') {
        return <Typography sx={{ fontSize: 34, lineHeight: 1 }}>{pair.emoji}</Typography>;
    }
    if (side === 'audio') {
        return <VolumeUpRoundedIcon sx={{ color: BLUE, fontSize: 28 }} />;
    }
    if (side === 'pinyin') {
        return <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 15, color: NAVY }}>{pair.pinyin}</Typography>;
    }
    if (side === 'english') {
        return <Typography sx={{ fontFamily: INTER, fontSize: 14, color: NAVY }}>{pair.english}</Typography>;
    }
    return <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 22, color: NAVY }}>{pair.hanzi}</Typography>;
}

// "Quiz: Matching Game" — tap a card, then its partner in the other column.
// left_match / right_match decide what each column shows; this mock covers the
// character / pinyin / english / image / audio combinations the teacher can pick.
export default function MatchGameExerciseCard({ exercise, onReady }: MatchGameExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const [selected, setSelected] = React.useState<{ side: 'left' | 'right'; pairIndex: number } | null>(null);
    const [matched, setMatched] = React.useState<number[]>([]);
    // "<side>-<pairIndex>" for the two cards of a failed attempt, so only the
    // tapped cards flash and not their partners in the other column.
    const [wrongTaps, setWrongTaps] = React.useState<string[]>([]);
    const timeoutRef = React.useRef<number>();

    React.useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const rightOrder = React.useMemo(() => shiftedOrder(exercise.pairs.length, exercise.id), [exercise.pairs.length, exercise.id]);

    const handleTap = (side: 'left' | 'right', pairIndex: number) => {
        if (matched.includes(pairIndex)) {
            return;
        }
        if (!selected || selected.side === side) {
            setSelected({ side, pairIndex });
            return;
        }
        if (selected.pairIndex === pairIndex) {
            const nextMatched = [...matched, pairIndex];
            setMatched(nextMatched);
            setSelected(null);
            if (nextMatched.length === exercise.pairs.length) {
                onReady();
            }
            return;
        }
        setSelected(null);
        setWrongTaps([`${selected.side}-${selected.pairIndex}`, `${side}-${pairIndex}`]);
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => setWrongTaps([]), WRONG_FLASH_MS);
    };

    const renderColumn = (side: 'left' | 'right', order: number[], matchType: MatchSide) => (
        <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
            {order.map(pairIndex => {
                const isMatched = matched.includes(pairIndex);
                const isSelected = selected?.side === side && selected.pairIndex === pairIndex;
                const isWrong = wrongTaps.includes(`${side}-${pairIndex}`);
                return (
                    <ButtonBase
                        key={`${side}-${exercise.pairs[pairIndex].hanzi}`}
                        onClick={() => handleTap(side, pairIndex)}
                        disabled={isMatched}
                        sx={{
                            width: '100%', minHeight: 64, px: 1.5, py: 1.25, borderRadius: '16px',
                            backgroundColor: isMatched ? `${GREEN}14` : isWrong ? `${RED}14` : SURFACE_SOFT,
                            border: `2px solid ${isMatched ? GREEN : isWrong ? RED : isSelected ? BLUE : 'transparent'}`,
                            opacity: isMatched ? 0.65 : 1,
                            transition: 'background-color 150ms ease, border-color 150ms ease, opacity 150ms ease',
                            textAlign: 'center',
                        }}
                    >
                        {renderSide(exercise.pairs[pairIndex], matchType)}
                        {isMatched && <CheckRoundedIcon sx={{ color: GREEN, fontSize: 18, ml: 0.75 }} />}
                    </ButtonBase>
                );
            })}
        </Stack>
    );

    const allMatched = matched.length === exercise.pairs.length;

    return (
        <Box sx={desktop ? { ...cardSx, p: 3 } : undefined}>
            <ExerciseLabel>MATCHING GAME</ExerciseLabel>
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, lineHeight: 1.35 }}>
                {exercise.prompt}
            </Typography>
            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.75 }}>
                Tap a card, then tap its match on the other side.
            </Typography>
            {desktop && (
                <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    {[exercise.leftMatch, exercise.rightMatch].map((side, index) => (
                        <Typography key={`${side}-${index}`} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.08em', color: SLATE, textAlign: 'center' }}>
                            {sideLabels[side]}
                        </Typography>
                    ))}
                </Box>
            )}
            <Stack direction="row" spacing={2} sx={{ mt: desktop ? 1 : 2.5 }}>
                {renderColumn('left', exercise.pairs.map((_, index) => index), exercise.leftMatch)}
                {renderColumn('right', rightOrder, exercise.rightMatch)}
            </Stack>
            <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: allMatched ? SUCCESS_TEXT : SLATE, mt: 2, textAlign: 'center' }}>
                {allMatched ? '全对了! Every pair matched.' : `${matched.length} of ${exercise.pairs.length} matched`}
            </Typography>
        </Box>
    );
}
